import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../contexts/ThemeContext'
import { assetLabel, cssToken, formatGap, formatKPIValue, type BenchmarkPoint, type Distribution } from './benchmark-utils'

/**
 * Gap to the median × money at stake (spec 2026-09-13-01, the portfolio view).
 *
 * The vertical axis carries the annual spend, not the number of units: units
 * are a stratum, and on an axis they would spend the strongest visual channel
 * on a covariate and manufacture culprits out of economies of scale. They only
 * size the bubbles. The top-left quadrant matters as much as the top-right one:
 * cheaper than the pack on a big budget is where the reference contracts are.
 */
interface Props {
    distribution: Distribution
    locale: string
    onSelect: (point: BenchmarkPoint) => void
}

export function DeviationSpendScatter({ distribution, locale, onSelect }: Props) {
    const { t } = useTranslation('benchmark')
    useTheme()
    const { kpi, currency } = distribution

    const option = useMemo(() => {
        const muted = cssToken('--muted-foreground', '#6b7280')
        const warning = cssToken('--warning', '#d97706')
        const border = cssToken('--border', '#e5e7eb')
        const foreground = cssToken('--foreground', '#0a0a0a')

        const toDatum = (p: BenchmarkPoint) => ({
            value: [(p.delta_vs_median ?? 0) * 100, p.numerator / 100],
            symbolSize: 6 + Math.sqrt(Math.max(p.context.units ?? 1, 1)) * 2.2,
            point: p,
        })
        const withGap = distribution.points.filter((p) => p.delta_vs_median !== null)
        const outliers = withGap.filter((p) => p.outlier === 'high')
        const subjects = withGap.filter((p) => p.outlier !== 'high')
        const moneyFmt = (v: number) =>
            currency
                ? new Intl.NumberFormat(locale, { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }).format(v)
                : new Intl.NumberFormat(locale, { notation: 'compact' }).format(v)

        const quadrant = (text: string, pos: Record<string, number | string>, strong = false) => ({
            type: 'text',
            ...pos,
            silent: true,
            style: { text, fill: strong ? warning : muted, fontSize: 11, fontWeight: strong ? 600 : 400 },
        })

        return {
            animation: false,
            grid: { left: 64, right: 24, top: 40, bottom: 48 },
            legend: {
                top: 0,
                right: 0,
                textStyle: { color: muted, fontSize: 11 },
                data: [t('scatter.legend_subjects'), t('scatter.legend_outliers')],
            },
            tooltip: {
                trigger: 'item',
                formatter: (params: { data: { point: BenchmarkPoint } }) => {
                    const p = params.data.point
                    const lines = [
                        `<strong>${assetLabel(p.asset)}</strong>`,
                        `${t('scatter.tooltip_value')} : ${formatKPIValue(kpi.unit, p.value, currency, locale)}`,
                        `${t('scatter.tooltip_gap')} : ${formatGap(p.delta_vs_median, locale)}`,
                        `${t('scatter.tooltip_spend')} : ${moneyFmt(p.numerator / 100)}`,
                    ]
                    if (p.context.units) lines.push(`${t('scatter.tooltip_units')} : ${p.context.units}`)
                    lines.push(`n = ${distribution.cohort.n}`)
                    return lines.join('<br/>')
                },
            },
            xAxis: {
                type: 'value',
                name: t('scatter.x_axis'),
                nameLocation: 'middle',
                nameGap: 30,
                nameTextStyle: { color: muted },
                // Hug the data: a default axis opened at −100 % crushes a portfolio
                // that sits within ±10 % of its median into one pixel column.
                // A tenth of headroom on the right keeps the outlier's bubble and
                // label inside the frame.
                min: (extent: { min: number }) => Math.floor(Math.min(extent.min, -5) / 10) * 10,
                max: (extent: { min: number; max: number }) =>
                    Math.ceil((Math.max(extent.max, 5) + (extent.max - Math.min(extent.min, 0)) * 0.1) / 10) * 10,
                axisLabel: { color: muted, formatter: (v: number) => `${v > 0 ? '+' : ''}${Math.round(v)} %` },
                splitLine: { lineStyle: { color: border, opacity: 0.5 } },
            },
            yAxis: {
                type: 'value',
                name: t('scatter.y_axis'),
                nameTextStyle: { color: muted, align: 'left' },
                axisLabel: { color: muted, formatter: moneyFmt },
                splitLine: { lineStyle: { color: border, opacity: 0.5 } },
            },
            graphic: [
                quadrant(t('scatter.quadrant.renegotiate'), { right: 32, top: 48 }, true),
                quadrant(t('scatter.quadrant.reference'), { left: 76, top: 48 }),
                quadrant(t('scatter.quadrant.small_gap'), { right: 32, bottom: 58 }),
                quadrant(t('scatter.quadrant.nothing'), { left: 76, bottom: 58 }),
            ],
            series: [
                {
                    name: t('scatter.legend_subjects'),
                    type: 'scatter',
                    data: subjects.map(toDatum),
                    itemStyle: { color: muted, opacity: 0.5, borderColor: cssToken('--background', '#fff'), borderWidth: 1.5 },
                    emphasis: { itemStyle: { opacity: 0.9 } },
                    markLine: {
                        silent: true,
                        symbol: 'none',
                        lineStyle: { color: foreground, opacity: 0.35, type: 'dashed' },
                        label: { show: false },
                        data: [
                            { xAxis: 0 },
                            ...(distribution.spend_median !== null ? [{ yAxis: distribution.spend_median / 100 }] : []),
                        ],
                    },
                },
                {
                    name: t('scatter.legend_outliers'),
                    type: 'scatter',
                    data: outliers.map(toDatum),
                    itemStyle: { color: warning, borderColor: cssToken('--background', '#fff'), borderWidth: 1.5 },
                    label: {
                        show: true,
                        position: 'left',
                        distance: 8,
                        color: foreground,
                        fontSize: 11,
                        fontWeight: 600,
                        formatter: (params: { data: { point: BenchmarkPoint } }) => params.data.point.asset.code || params.data.point.asset.name,
                    },
                },
            ],
        }
    }, [distribution, currency, kpi.unit, locale, t])

    return (
        <ReactECharts
            option={option}
            style={{ height: 380, width: '100%' }}
            notMerge
            onEvents={{ click: (params: { data?: { point?: BenchmarkPoint } }) => params.data?.point && onSelect(params.data.point) }}
            opts={{ renderer: 'canvas' }}
        />
    )
}
